import React, { useEffect, useRef, useState } from "react";

const useCountUp = (end, duration = 2000, startOnView = true) => {
    const [count, setCount] = useState(0);
    const [started, setStarted] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        if (!startOnView) {
            setStarted(true);
            return;
        }
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && !started) {
                    setStarted(true);
                    observer.disconnect();
                }
            },
            { threshold: 0.3 }
        );
        if (ref.current) observer.observe(ref.current);
        return () => observer.disconnect();
    }, [startOnView, started]);

    useEffect(() => {
        if (!started) return;
        let startTime = null;
        let rafId;

        const tick = (timestamp) => {
            if (!startTime) startTime = timestamp;
            const progress = Math.min((timestamp - startTime) / duration, 1);
            // Ease out cubic
            const eased = 1 - Math.pow(1 - progress, 3);
            setCount(Math.floor(eased * end));
            if (progress < 1) {
                rafId = requestAnimationFrame(tick);
            }
        };

        rafId = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(rafId);
    }, [started, end, duration]);

    return { count, ref };
};

const FadeIn = ({ children, delay = 0, className = "" }) => {
    const [visible, setVisible] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setVisible(true);
                    observer.disconnect();
                }
            },
            { threshold: 0.1 }
        );
        if (ref.current) observer.observe(ref.current);
        return () => observer.disconnect();
    }, []);

    return (
        <div
            ref={ref}
            className={`transition-all duration-700 ease-out ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"} ${className}`}
            style={{ transitionDelay: `${delay}ms` }}
        >
            {children}
        </div>
    );
};

const StatCard = ({ icon, value, suffix, label, delay }) => {
    const { count, ref } = useCountUp(value, 2500);

    return (
        <FadeIn delay={delay}>
            <div
                ref={ref}
                className="group relative rounded-2xl bg-slate-900/60 border border-slate-800 p-6 md:p-8 hover:border-amber-500/30 hover:-translate-y-1 transition-all duration-300 shadow-xl"
            >
                {/* Subtle gradient glow on hover */}
                <div className="absolute inset-0 bg-gradient-to-br from-amber-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl" />

                <div className="relative z-10 flex flex-col items-center justify-center text-center">
                    <div className="text-3xl md:text-5xl font-black tracking-tight flex items-center justify-center gap-2 mb-3 text-white">
                        <div className="text-amber-500">
                            <i className={`bi ${icon}`} aria-hidden="true" />
                        </div>
                        {count}
                        <span className="text-amber-500">{suffix}</span>
                    </div>

                    <div className="text-xs md:text-sm text-slate-400 uppercase tracking-widest font-semibold">
                        {label}
                    </div>
                </div>
            </div>
        </FadeIn>
    );
};

export default function Stats() {
    const stats = [
        { icon: "bi-check-circle-fill", value: 150, suffix: "+", label: "Projects Completed" },
        { icon: "bi-people-fill", value: 50, suffix: "+", label: "Happy Clients" },
        { icon: "bi-clock-history", value: 5, suffix: "+", label: "Years of Experience" },
        { icon: "bi-person-check", value: 90, suffix: "%", label: "Client Satisfaction" },
    ];

    return (
        <section className="relative bg-black overflow-hidden py-2">
            {/* Ambient background */}
            <div className="" />

            <div className=" max-w-7xl mx-auto relative z-10">
                {/* Header */}
                <div className="text-center max-w-2xl mx-auto mb-16">
                    <FadeIn>
                        <span className="inline-flex items-center gap-2 text-amber-500 uppercase tracking-widest text-sm font-semibold font-heading mb-4">
                            <span className="w-8 h-[1px] bg-amber-500/50" />
                            Our Impact
                            <span className="w-8 h-[1px] bg-amber-500/50" />
                        </span>
                    </FadeIn>

                    <FadeIn delay={100}>
                        <h2 className="text-4xl md:text-5xl font-black font-heading text-white mb-4">
                            Numbers That <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-600">Speak</span>
                        </h2>
                    </FadeIn>

                    <FadeIn delay={200}>
                        <p className="text-lg text-slate-400 leading-relaxed">
                            Real results from real partnerships. Here's what we've achieved
                            helping businesses across East Africa transform digitally.
                        </p>
                    </FadeIn>
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                    {stats.map((stat, index) => (
                        <StatCard key={stat.label} {...stat} delay={index * 100} />
                    ))}
                </div>

                {/* Bottom trust bar */}
                <FadeIn delay={500}>
                    <div className="mt-16 flex flex-wrap items-center justify-center gap-8 text-slate-600">
                        <div className="flex items-center gap-2 text-sm">
                            <i className="bi bi-shield-check text-emerald-500" aria-hidden="true" />
                            <span>ISO Compliant</span>
                        </div>
                        <div className="w-1 h-1 rounded-full bg-slate-700 hidden sm:block" />
                        <div className="flex items-center gap-2 text-sm">
                            <i className="bi bi-award text-amber-500" aria-hidden="true" />
                            <span>URA Certified Partner</span>
                        </div>
                        <div className="w-1 h-1 rounded-full bg-slate-700 hidden sm:block" />
                        <div className="flex items-center gap-2 text-sm">
                            <i className="bi bi-globe text-blue-400" aria-hidden="true" />
                            <span>East Africa Coverage</span>
                        </div>
                    </div>
                </FadeIn>
            </div>
            {/* Bottom Fade */}
            <div className="
        absolute
        bottom-0
        left-0
        w-full
        h-32
        bg-gradient-to-t
        from-slate-950
        to-black
    "/>
        </section>
    );
}